import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Drawer,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import theme from '../../../theme';
import { commonStyles } from '../../../utils/styles/inputs';
import { useStores } from '../../../hooks/useStores';
import { useUser } from '../../../hooks/useUser';

interface AudioUploadConfig {
  transcription_service: string;
  GEMINI_TRANSCRIPTION_MODEL?: string;
  OPENAI_TRANSCRIPTION_MODEL?: string;
  GPT_ANALYSIS_MODEL: string;
  recorded_on: string;
  max_workers: string;
  batch_wait_time: string;
  storeName: string;
}

interface AudioUploadConfigurationDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: AudioUploadConfig) => void;
  initialConfig?: AudioUploadConfig | null;
}

const AudioUploadConfigurationDrawer: React.FC<
  AudioUploadConfigurationDrawerProps
> = ({ open, onClose, onSave, initialConfig }) => {
  const { user } = useUser();
  const { data: storesData, storesListLoading } = useStores(
    user?.tenantName || ''
  );
  const stores = useMemo(() => storesData?.data?.stores || [], [storesData]);

  const formatDateToString = (date: Date | null): string => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDateString = (dateString: string): Date | null => {
    if (!dateString) return new Date();
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return new Date();
  };

  const geminiModels = [
    'gemini-2.5-flash',
    'gemini-2.5-pro',
    'gemini-3-pro-preview',
    'gemini-2.0-flash',
    'gemini-2.0-flash-exp',
  ];

  const openaiModels = ['gpt-4o-transcribe-diarize'];

  const gptAnalysisModels = [
    'gpt-4o-mini',
    'gpt-4o',
    'gpt-4.1',
    'gpt-4.1-mini',
    'gpt-5-mini',
    'gpt-5',
    'gpt-5.1',
  ];

  const [recordedOnDate, setRecordedOnDate] = useState<Date | null>(new Date());
  const [config, setConfig] = useState<AudioUploadConfig>({
    transcription_service: 'gemini',
    GEMINI_TRANSCRIPTION_MODEL: 'gemini-2.5-flash',
    GPT_ANALYSIS_MODEL: 'gpt-4o-mini',
    recorded_on: formatDateToString(new Date()),
    max_workers: '5',
    batch_wait_time: '60',
    storeName: '',
  });

  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
      setRecordedOnDate(parseDateString(initialConfig.recorded_on));
    } else {
      // Reset to defaults when no initial config
      const defaultStoreName = stores.length > 0 ? stores[0].friendly_name : '';
      setConfig({
        transcription_service: 'gemini',
        GEMINI_TRANSCRIPTION_MODEL: 'gemini-2.5-flash',
        GPT_ANALYSIS_MODEL: 'gpt-4o-mini',
        recorded_on: formatDateToString(new Date()),
        max_workers: '5',
        batch_wait_time: '60',
        storeName: defaultStoreName,
      });
      setRecordedOnDate(new Date());
    }
  }, [initialConfig, stores]);

  const handleChange = (field: keyof AudioUploadConfig, value: string) => {
    setConfig(prev => {
      const updated = { ...prev, [field]: value };

      // When transcription service changes, reset the model
      if (field === 'transcription_service') {
        if (value === 'gemini') {
          updated.GEMINI_TRANSCRIPTION_MODEL = 'gemini-2.5-flash';
          updated.OPENAI_TRANSCRIPTION_MODEL = undefined;
        } else if (value === 'openai') {
          updated.OPENAI_TRANSCRIPTION_MODEL = 'gpt-4o-transcribe-diarize';
          updated.GEMINI_TRANSCRIPTION_MODEL = undefined;
        }
      }

      return updated;
    });
  };

  const handleDateChange = (date: Date | null) => {
    setRecordedOnDate(date);
    if (date) {
      handleChange('recorded_on', formatDateToString(date));
    }
  };

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const commonSelectStyles = {
    '& .MuiSelect-select': {
      fontSize: '14px',
      fontWeight: 400,
      background: '#F8F9FC',
      display: 'flex',
      padding: '8px 12px',
      alignItems: 'center',
      borderRadius: '6px',
      border: '1px solid #DCE0E7',
      lineHeight: 'normal',
      letterSpacing: '-0.14px',
      color: theme.palette.lightGray.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '500px',
          height: '100%',
          backgroundColor: '#FFF',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        },
      }}
    >
      <Box sx={{ width: '100%', bgcolor: '#fff', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            px: 2,
            py: 1,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={onClose}>
              <XIcon size={20} />
            </IconButton>
            <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
              Audio Upload Configuration
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '6px',
              boxShadow: 'none',
            }}
          >
            Save Config
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 1,
            px: 2,
          }}
        >
          <FormControl fullWidth size="small">
            <FormLabel sx={commonStyles.formLabel}>Select Store</FormLabel>
            <Select
              value={config.storeName}
              sx={commonSelectStyles}
              inputProps={{ 'aria-label': 'Without label' }}
              onChange={e => handleChange('storeName', e.target.value)}
              disabled={storesListLoading || stores.length === 0}
              displayEmpty
            >
              {storesListLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading stores...
                </MenuItem>
              ) : stores.length === 0 ? (
                <MenuItem disabled value="">
                  No stores available
                </MenuItem>
              ) : (
                stores.map((store: any) => (
                  <MenuItem key={store.id} value={store.friendly_name}>
                    {store.friendly_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <FormLabel sx={commonStyles.formLabel}>
              Transcription Service
            </FormLabel>
            <Select
              value={config.transcription_service}
              sx={commonSelectStyles}
              inputProps={{ 'aria-label': 'Without label' }}
              onChange={e =>
                handleChange('transcription_service', e.target.value)
              }
            >
              <MenuItem value="gemini">Gemini</MenuItem>
              <MenuItem value="openai">OpenAI</MenuItem>
            </Select>
          </FormControl>

          {config.transcription_service === 'gemini' && (
            <FormControl fullWidth size="small">
              <FormLabel sx={commonStyles.formLabel}>
                Gemini Transcription Model
              </FormLabel>
              <Select
                value={config.GEMINI_TRANSCRIPTION_MODEL || 'gemini-2.5-flash'}
                sx={commonSelectStyles}
                inputProps={{ 'aria-label': 'Without label' }}
                onChange={e =>
                  handleChange('GEMINI_TRANSCRIPTION_MODEL', e.target.value)
                }
              >
                {geminiModels.map(model => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {config.transcription_service === 'openai' && (
            <FormControl fullWidth size="small">
              <FormLabel sx={commonStyles.formLabel}>
                OpenAI Transcription Model
              </FormLabel>
              <Select
                value={
                  config.OPENAI_TRANSCRIPTION_MODEL ||
                  'gpt-4o-transcribe-diarize'
                }
                sx={commonSelectStyles}
                inputProps={{ 'aria-label': 'Without label' }}
                onChange={e =>
                  handleChange('OPENAI_TRANSCRIPTION_MODEL', e.target.value)
                }
              >
                {openaiModels.map(model => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth size="small">
            <FormLabel sx={commonStyles.formLabel}>
              GPT Analysis Model
            </FormLabel>
            <Select
              value={config.GPT_ANALYSIS_MODEL}
              sx={commonSelectStyles}
              inputProps={{ 'aria-label': 'Without label' }}
              onChange={e => handleChange('GPT_ANALYSIS_MODEL', e.target.value)}
            >
              {gptAnalysisModels.map(model => (
                <MenuItem key={model} value={model}>
                  {model}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel sx={commonStyles.formLabel}>Recorded On</FormLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={recordedOnDate}
                views={['year', 'month', 'day']}
                onChange={handleDateChange}
                format="dd-MM-yyyy"
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    sx: commonStyles.textField,
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel sx={commonStyles.formLabel}>Max Workers</FormLabel>
            <TextField
              type="number"
              value={config.max_workers}
              onChange={e => handleChange('max_workers', e.target.value)}
              fullWidth
              variant="outlined"
              inputProps={{ min: 1 }}
              sx={commonStyles.textField}
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel sx={commonStyles.formLabel}>Batch Wait Time</FormLabel>
            <TextField
              type="number"
              value={config.batch_wait_time}
              onChange={e => handleChange('batch_wait_time', e.target.value)}
              fullWidth
              variant="outlined"
              inputProps={{ min: 1 }}
              sx={commonStyles.textField}
            />
          </FormControl>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AudioUploadConfigurationDrawer;
